import { fetchClient } from "@/api/api";
import type {
	CreateTrustedIP,
	ReassignWorkersOutput,
	ServersOverviewOutput,
	SetServerStatusInput,
	TrustedIPOutput,
	UpdateServerInput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SERVER_ADMIN_SERVICE_KEYS } from "./keys";

const invalidateOverview = () =>
	invalidateQueries(SERVER_ADMIN_SERVICE_KEYS.useGetServersOverview());

export const useGetServersOverview: CustomQueryHook<
	ServersOverviewOutput,
	undefined,
	ServersOverviewOutput
> = (options) => {
	return useQuery({
		...options,
		queryKey: SERVER_ADMIN_SERVICE_KEYS.useGetServersOverview(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/admin/server/", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

export const useCreateServer: CustomMutationHook<
	CreateTrustedIP,
	TrustedIPOutput
> = (mutationOptions) => {
	const { toastApiError } = useApiError();
	return useMutation<TrustedIPOutput, ApiError, CreateTrustedIP>({
		...mutationOptions,
		mutationKey: SERVER_ADMIN_SERVICE_KEYS.useCreateServer(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/admin/server/", {
				body,
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		onSuccess: (data, variables, onMutateResult, mutationContext) => {
			invalidateOverview();
			mutationOptions?.onSuccess?.(
				data,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			toastApiError(error);
			mutationOptions?.onError?.(
				error,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
	});
};

export const useUpdateServer: CustomMutationHook<
	UpdateServerInput,
	TrustedIPOutput
> = (mutationOptions) => {
	const { toastApiError } = useApiError();
	return useMutation<TrustedIPOutput, ApiError, UpdateServerInput>({
		...mutationOptions,
		mutationKey: SERVER_ADMIN_SERVICE_KEYS.useUpdateServer(),
		mutationFn: async ({ ip, body }) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/admin/server/{ip}",
				{ params: { path: { ip } }, body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onSuccess: (data, variables, onMutateResult, mutationContext) => {
			invalidateOverview();
			mutationOptions?.onSuccess?.(
				data,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			toastApiError(error);
			mutationOptions?.onError?.(
				error,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
	});
};

export const useSetServerStatus: CustomMutationHook<
	SetServerStatusInput,
	TrustedIPOutput
> = (mutationOptions) => {
	const { toastApiError } = useApiError();
	return useMutation<TrustedIPOutput, ApiError, SetServerStatusInput>({
		...mutationOptions,
		mutationKey: SERVER_ADMIN_SERVICE_KEYS.useSetServerStatus(),
		mutationFn: async ({ ip, body }) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/admin/server/{ip}/status",
				{ params: { path: { ip } }, body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onSuccess: (data, variables, onMutateResult, mutationContext) => {
			invalidateOverview();
			mutationOptions?.onSuccess?.(
				data,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			toastApiError(error);
			mutationOptions?.onError?.(
				error,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
	});
};

export const useReassignWorkers: CustomMutationHook<
	void,
	ReassignWorkersOutput
> = (mutationOptions) => {
	const { toastApiError } = useApiError();
	return useMutation<ReassignWorkersOutput, ApiError, void>({
		...mutationOptions,
		mutationKey: SERVER_ADMIN_SERVICE_KEYS.useReassignWorkers(),
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/admin/server/reassign",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onSuccess: (data, variables, onMutateResult, mutationContext) => {
			invalidateOverview();
			mutationOptions?.onSuccess?.(
				data,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
		onError: (error, variables, onMutateResult, mutationContext) => {
			toastApiError(error);
			mutationOptions?.onError?.(
				error,
				variables,
				onMutateResult,
				mutationContext,
			);
		},
	});
};
